import React, { useState } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, getEventByID, getUserProfile } from '../../firebase';

const RatingModificationHistory = ({ courseId, eventId }) => {
    const [showHistory, setShowHistory] = useState(false);
    const [ratingHistory, setRatingHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [sortBy, setSortBy] = useState('time');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedStudent, setSelectedStudent] = useState('all');

    const fetchRatingHistory = async () => {
        const filterField = eventId ? 'eventId' : 'courseId';
        const filterValue = eventId || courseId;
        if (!filterValue) return;

        setIsLoadingHistory(true);
        try {
            const historyRef = collection(db, 'RatingHistory');
            const q = query(
                historyRef,
                where(filterField, '==', filterValue)
            );
            const querySnapshot = await getDocs(q);
            const history = [];
            querySnapshot.forEach((doc) => {
                history.push({ id: doc.id, ...doc.data() });
            });

            const enrichedHistory = await Promise.all(
                history.map(async (record) => {
                    const enrichedRecord = { ...record };
                    if (!record.eventTitle && record.eventId) {
                        try {
                            const eventData = await getEventByID(record.eventId);
                            if (eventData) {
                                enrichedRecord.eventTitle = eventData.title || 'Untitled Activity';
                                enrichedRecord.eventDate = eventData.date || 'N/A';
                            }
                        } catch (error) {
                            console.error(`Failed to fetch event ${record.eventId}:`, error);
                        }
                    }
                    if (!record.raterName && record.raterEmail) {
                        try {
                            const userProfile = await getUserProfile(record.raterEmail);
                            if (userProfile) {
                                enrichedRecord.raterName = userProfile.name || userProfile.displayName || record.raterEmail;
                            }
                        } catch (error) {
                            console.error(`Failed to fetch user profile for ${record.raterEmail}:`, error);
                        }
                    }
                    return enrichedRecord;
                })
            );
            enrichedHistory.sort((a, b) => {
                const aTime = a.modifiedAt?.toDate?.()?.getTime() ?? 0;
                const bTime = b.modifiedAt?.toDate?.()?.getTime() ?? 0;
                return bTime - aTime;
            });
            setRatingHistory(enrichedHistory);
        } catch (error) {
            console.error('Error fetching rating history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleToggleHistory = () => {
        const newShowHistory = !showHistory;
        setShowHistory(newShowHistory);
        if (newShowHistory && ratingHistory.length === 0) {
            fetchRatingHistory();
        }
    };

    const sortedByTime = [...ratingHistory].sort((a, b) => {
        const aTime = a.modifiedAt?.toDate?.()?.getTime() ?? 0;
        const bTime = b.modifiedAt?.toDate?.()?.getTime() ?? 0;
        return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
    });

    // unique students sorted alphabetically
    const uniqueStudents = [...new Map(
        ratingHistory.map(r => [r.raterEmail, { email: r.raterEmail, name: r.raterName || r.raterEmail }])
    ).values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    // grouped by student, filtered by selectedStudent
    const groupedByStudent = uniqueStudents
        .filter(s => selectedStudent === 'all' || s.email === selectedStudent)
        .map(student => ({
            ...student,
            records: sortedByTime.filter(r => r.raterEmail === student.email),
        }));

    return (
        <Section>
            <SectionTitleContainer>
                <SectionTitle>
                    Rating Modification History
                    <InfoIconContainer
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showTooltip && (
                            <Tooltip>
                                View the complete history of all rating modifications made by students, including timestamps, reasons, and original values.
                            </Tooltip>
                        )}
                    </InfoIconContainer>
                </SectionTitle>
                <HeaderRight>
                    {showHistory && (
                        <>
                            <SortButtons>
                                <SortButton $active={sortBy === 'time'} onClick={() => setSortBy('time')}>By Time</SortButton>
                                <SortButton $active={sortBy === 'student'} onClick={() => setSortBy('student')}>By Student</SortButton>
                            </SortButtons>
                            <ContextControl>
                                {sortBy === 'time' ? (
                                    <SortButtons style={{ width: '100%' }}>
                                        <SortButton $active={sortOrder === 'desc'} onClick={() => setSortOrder('desc')} style={{ flex: 1 }}>↓ Newest</SortButton>
                                        <SortButton $active={sortOrder === 'asc'} onClick={() => setSortOrder('asc')} style={{ flex: 1 }}>↑ Oldest</SortButton>
                                    </SortButtons>
                                ) : (
                                    <StudentSelect
                                        value={selectedStudent}
                                        onChange={e => setSelectedStudent(e.target.value)}
                                    >
                                        <option value="all">All Students ({uniqueStudents.length})</option>
                                        {uniqueStudents.map(s => (
                                            <option key={s.email} value={s.email}>{s.name}</option>
                                        ))}
                                    </StudentSelect>
                                )}
                            </ContextControl>
                        </>
                    )}
                    <ToggleButton onClick={handleToggleHistory}>
                        {showHistory ? 'Hide History' : 'Show History'}
                    </ToggleButton>
                </HeaderRight>
            </SectionTitleContainer>

            {showHistory && (
                <>
                    {isLoadingHistory ? (
                        <LoadingText>Loading history...</LoadingText>
                    ) : ratingHistory.length === 0 ? (
                        <NoHistoryText>No rating modifications recorded yet.</NoHistoryText>
                    ) : (
                        <HistoryList>
                            {sortBy === 'student' ? groupedByStudent.map(student => (
                                <StudentGroup key={student.email}>
                                    <StudentGroupHeader>
                                        <StudentGroupName>{student.name}</StudentGroupName>
                                        <StudentGroupEmail>{student.email}</StudentGroupEmail>
                                        <StudentGroupCount>{student.records.length} modification{student.records.length !== 1 ? 's' : ''}</StudentGroupCount>
                                    </StudentGroupHeader>
                                    {student.records.map(record => (
                                        <HistoryItem key={record.id} $inGroup>
                                            <HistoryHeader>
                                                <HistoryHeaderLeft>
                                                    {record.eventTitle && (
                                                        <EventInfo>
                                                            Activity: {record.eventTitle}
                                                            {record.eventDate && ` • ${record.eventDate}`}
                                                        </EventInfo>
                                                    )}
                                                </HistoryHeaderLeft>
                                                <HistoryDate>
                                                    {record.modifiedAt?.toDate?.()?.toLocaleDateString('en-US', {
                                                        month: 'short', day: 'numeric', year: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    }) || 'N/A'}
                                                </HistoryDate>
                                            </HistoryHeader>
                                            <HistoryReason>
                                                <ReasonLabel>Modification Reason:</ReasonLabel>
                                                <ReasonText>{record.modificationReason}</ReasonText>
                                            </HistoryReason>
                                            <HistoryChanges>
                                                <ChangeColumn>
                                                    <ChangeLabel>Previous Ratings</ChangeLabel>
                                                    <RatingGrid>
                                                        <RatingItem>
                                                            <RatingDimension>Nature:</RatingDimension>
                                                            <RatingScore>{record.previousRatings?.importance?.score || 'N/A'}/4</RatingScore>
                                                            <RatingLabel>({record.previousRatings?.importance?.label || 'N/A'})</RatingLabel>
                                                        </RatingItem>
                                                        <RatingItem>
                                                            <RatingDimension>Quality:</RatingDimension>
                                                            <RatingScore>{record.previousRatings?.quality?.score || 'N/A'}/4</RatingScore>
                                                            <RatingLabel>({record.previousRatings?.quality?.label || 'N/A'})</RatingLabel>
                                                        </RatingItem>
                                                        <RatingItem>
                                                            <RatingDimension>Volume:</RatingDimension>
                                                            <RatingScore>{record.previousRatings?.volume?.score || 'N/A'}/4</RatingScore>
                                                            <RatingLabel>({record.previousRatings?.volume?.label || 'N/A'})</RatingLabel>
                                                        </RatingItem>
                                                    </RatingGrid>
                                                </ChangeColumn>
                                                <ChangeArrow>→</ChangeArrow>
                                                <ChangeColumn>
                                                    <ChangeLabel>Updated Ratings</ChangeLabel>
                                                    <RatingGrid>
                                                        <RatingItem>
                                                            <RatingDimension>Nature:</RatingDimension>
                                                            <RatingScore $isChanged={record.previousRatings?.importance?.score !== record.newRatings?.importance?.score}>
                                                                {record.newRatings?.importance?.score || 'N/A'}/4
                                                            </RatingScore>
                                                            <RatingLabel>({record.newRatings?.importance?.label || 'N/A'})</RatingLabel>
                                                        </RatingItem>
                                                        <RatingItem>
                                                            <RatingDimension>Quality:</RatingDimension>
                                                            <RatingScore $isChanged={record.previousRatings?.quality?.score !== record.newRatings?.quality?.score}>
                                                                {record.newRatings?.quality?.score || 'N/A'}/4
                                                            </RatingScore>
                                                            <RatingLabel>({record.newRatings?.quality?.label || 'N/A'})</RatingLabel>
                                                        </RatingItem>
                                                        <RatingItem>
                                                            <RatingDimension>Volume:</RatingDimension>
                                                            <RatingScore $isChanged={record.previousRatings?.volume?.score !== record.newRatings?.volume?.score}>
                                                                {record.newRatings?.volume?.score || 'N/A'}/4
                                                            </RatingScore>
                                                            <RatingLabel>({record.newRatings?.volume?.label || 'N/A'})</RatingLabel>
                                                        </RatingItem>
                                                    </RatingGrid>
                                                </ChangeColumn>
                                            </HistoryChanges>
                                            {(record.previousRatings?.comment || record.newRatings?.comment) &&
                                             (record.previousRatings?.comment !== record.newRatings?.comment) && (
                                                <CommentsComparison>
                                                    <ChangeLabel>Comments Changes</ChangeLabel>
                                                    <CommentsGrid>
                                                        <CommentColumn>
                                                            <CommentHeader>Previous Comment:</CommentHeader>
                                                            <CommentText>{record.previousRatings?.comment || '(No comment)'}</CommentText>
                                                        </CommentColumn>
                                                        <ChangeArrow>→</ChangeArrow>
                                                        <CommentColumn>
                                                            <CommentHeader $isChanged={true}>Updated Comment:</CommentHeader>
                                                            <CommentText $isChanged={true}>{record.newRatings?.comment || '(No comment)'}</CommentText>
                                                        </CommentColumn>
                                                    </CommentsGrid>
                                                </CommentsComparison>
                                            )}
                                        </HistoryItem>
                                    ))}
                                </StudentGroup>
                            )) : sortedByTime.map((record) => (
                                <HistoryItem key={record.id}>
                                    <HistoryHeader>
                                        <HistoryHeaderLeft>
                                            <HistoryStudent>
                                                {record.raterName ? (
                                                    <>
                                                        <RaterName>{record.raterName}</RaterName>
                                                        <RaterEmail>({record.raterEmail})</RaterEmail>
                                                    </>
                                                ) : (
                                                    record.raterEmail
                                                )}
                                            </HistoryStudent>
                                            {record.eventTitle && (
                                                <EventInfo>
                                                    Activity: {record.eventTitle}
                                                    {record.eventDate && ` • ${record.eventDate}`}
                                                </EventInfo>
                                            )}
                                        </HistoryHeaderLeft>
                                        <HistoryDate>
                                            {record.modifiedAt?.toDate?.()?.toLocaleDateString('en-US', {
                                                month: 'short', day: 'numeric', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit'
                                            }) || 'N/A'}
                                        </HistoryDate>
                                    </HistoryHeader>

                                    <HistoryReason>
                                        <ReasonLabel>Modification Reason:</ReasonLabel>
                                        <ReasonText>{record.modificationReason}</ReasonText>
                                    </HistoryReason>

                                    <HistoryChanges>
                                        <ChangeColumn>
                                            <ChangeLabel>Previous Ratings</ChangeLabel>
                                            <RatingGrid>
                                                <RatingItem>
                                                    <RatingDimension>Nature:</RatingDimension>
                                                    <RatingScore>{record.previousRatings?.importance?.score || 'N/A'}/4</RatingScore>
                                                    <RatingLabel>({record.previousRatings?.importance?.label || 'N/A'})</RatingLabel>
                                                </RatingItem>
                                                <RatingItem>
                                                    <RatingDimension>Quality:</RatingDimension>
                                                    <RatingScore>{record.previousRatings?.quality?.score || 'N/A'}/4</RatingScore>
                                                    <RatingLabel>({record.previousRatings?.quality?.label || 'N/A'})</RatingLabel>
                                                </RatingItem>
                                                <RatingItem>
                                                    <RatingDimension>Volume:</RatingDimension>
                                                    <RatingScore>{record.previousRatings?.volume?.score || 'N/A'}/4</RatingScore>
                                                    <RatingLabel>({record.previousRatings?.volume?.label || 'N/A'})</RatingLabel>
                                                </RatingItem>
                                            </RatingGrid>
                                        </ChangeColumn>
                                        <ChangeArrow>→</ChangeArrow>
                                        <ChangeColumn>
                                            <ChangeLabel>Updated Ratings</ChangeLabel>
                                            <RatingGrid>
                                                <RatingItem>
                                                    <RatingDimension>Nature:</RatingDimension>
                                                    <RatingScore $isChanged={record.previousRatings?.importance?.score !== record.newRatings?.importance?.score}>
                                                        {record.newRatings?.importance?.score || 'N/A'}/4
                                                    </RatingScore>
                                                    <RatingLabel>({record.newRatings?.importance?.label || 'N/A'})</RatingLabel>
                                                </RatingItem>
                                                <RatingItem>
                                                    <RatingDimension>Quality:</RatingDimension>
                                                    <RatingScore $isChanged={record.previousRatings?.quality?.score !== record.newRatings?.quality?.score}>
                                                        {record.newRatings?.quality?.score || 'N/A'}/4
                                                    </RatingScore>
                                                    <RatingLabel>({record.newRatings?.quality?.label || 'N/A'})</RatingLabel>
                                                </RatingItem>
                                                <RatingItem>
                                                    <RatingDimension>Volume:</RatingDimension>
                                                    <RatingScore $isChanged={record.previousRatings?.volume?.score !== record.newRatings?.volume?.score}>
                                                        {record.newRatings?.volume?.score || 'N/A'}/4
                                                    </RatingScore>
                                                    <RatingLabel>({record.newRatings?.volume?.label || 'N/A'})</RatingLabel>
                                                </RatingItem>
                                            </RatingGrid>
                                        </ChangeColumn>
                                    </HistoryChanges>

                                    {(record.previousRatings?.comment || record.newRatings?.comment) &&
                                     (record.previousRatings?.comment !== record.newRatings?.comment) && (
                                        <CommentsComparison>
                                            <ChangeLabel>Comments Changes</ChangeLabel>
                                            <CommentsGrid>
                                                <CommentColumn>
                                                    <CommentHeader>Previous Comment:</CommentHeader>
                                                    <CommentText>{record.previousRatings?.comment || '(No comment)'}</CommentText>
                                                </CommentColumn>
                                                <ChangeArrow>→</ChangeArrow>
                                                <CommentColumn>
                                                    <CommentHeader $isChanged={true}>Updated Comment:</CommentHeader>
                                                    <CommentText $isChanged={true}>{record.newRatings?.comment || '(No comment)'}</CommentText>
                                                </CommentColumn>
                                            </CommentsGrid>
                                        </CommentsComparison>
                                    )}
                                </HistoryItem>
                            ))}
                        </HistoryList>
                    )}
                </>
            )}
        </Section>
    );
};

const Section = styled.div`
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    overflow-x: auto;
`;

const SectionTitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    min-width: 760px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 14px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const Tooltip = styled.div`
    position: absolute;
    top: 25px;
    left: -100px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px;
    width: 280px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 12px;
    line-height: 1.5;
    color: #333;
    text-align: left;
    font-weight: 400;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SortButtons = styled.div`
    display: flex;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
`;

const SortButton = styled.button`
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    background: ${props => props.$active ? '#3b82f6' : 'white'};
    color: ${props => props.$active ? 'white' : '#6b7280'};
    transition: background 0.2s, color 0.2s;

    &:hover {
        background: ${props => props.$active ? '#2563eb' : '#f3f4f6'};
    }
`;

const ToggleButton = styled.button`
    padding: 8px 16px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: #2563eb;
    }
`;

const LoadingText = styled.p`
    text-align: center;
    color: #6b7280;
    font-size: 14px;
    padding: 20px;
`;

const NoHistoryText = styled.p`
    text-align: center;
    color: #6b7280;
    font-size: 14px;
    padding: 20px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
`;

const HistoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
    min-width: 760px;
`;


const ContextControl = styled.div`
    width: 160px;
    flex-shrink: 0;
    display: flex;
`;

const StudentSelect = styled.select`
    padding: 6px 10px;
    font-size: 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    color: #374151;
    cursor: pointer;
    outline: none;
    width: 100%;

    &:focus {
        border-color: #3b82f6;
    }
`;

const StudentGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StudentGroupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    margin-top: 8px;
`;

const StudentGroupName = styled.span`
    font-weight: 700;
    font-size: 14px;
    color: #1d4ed8;
`;

const StudentGroupEmail = styled.span`
    font-size: 12px;
    color: #6b7280;
`;

const StudentGroupCount = styled.span`
    margin-left: auto;
    font-size: 11px;
    font-weight: 600;
    color: #3b82f6;
    background: #dbeafe;
    padding: 2px 8px;
    border-radius: 12px;
`;

const HistoryItem = styled.div`
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    ${props => props.$inGroup && 'margin-left: 16px;'}
`;

const HistoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
`;

const HistoryHeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const HistoryStudent = styled.span`
    font-weight: 600;
    font-size: 15px;
    color: #111827;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const RaterName = styled.span`
    font-weight: 600;
    color: #111827;
`;

const RaterEmail = styled.span`
    font-weight: 400;
    font-size: 13px;
    color: #6b7280;
`;

const EventInfo = styled.span`
    font-size: 12px;
    color: #6b7280;
`;

const HistoryDate = styled.span`
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
`;

const HistoryReason = styled.div`
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    padding: 16px;
    margin: 12px 0;
    border-radius: 8px;
`;

const ReasonLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    margin-bottom: 4px;
`;

const ReasonText = styled.div`
    font-size: 13px;
    color: #374151;
    line-height: 1.5;
`;

const HistoryChanges = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    background: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
`;

const ChangeColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    align-items: center;
`;

const ChangeLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    margin-bottom: 4px;
`;

const RatingGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RatingItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RatingDimension = styled.span`
    font-size: 12px;
    color: #6b7280;
    min-width: 80px;
`;

const RatingScore = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.$isChanged ? '#059669' : '#111827'};
    background: ${props => props.$isChanged ? '#d1fae5' : 'transparent'};
    padding: ${props => props.$isChanged ? '2px 6px' : '0'};
    border-radius: 4px;
    min-width: 36px;
    text-align: center;
`;

const RatingLabel = styled.span`
    font-size: 11px;
    color: #9ca3af;
`;

const ChangeArrow = styled.div`
    font-size: 24px;
    color: #9ca3af;
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

const CommentsComparison = styled.div`
    background: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    margin-top: 12px;
`;

const CommentsGrid = styled.div`
    display: flex;
    align-items: stretch;
    gap: 16px;
    margin-top: 8px;
`;

const CommentColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
`;

const CommentHeader = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${props => props.$isChanged ? '#10b981' : '#6b7280'};
    text-transform: uppercase;
`;

const CommentText = styled.div`
    font-size: 13px;
    color: ${props => props.$isChanged ? '#059669' : '#374151'};
    background: white;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid ${props => props.$isChanged ? '#10b981' : '#e5e7eb'};
    line-height: 1.5;
    word-wrap: break-word;
    white-space: pre-wrap;
    min-height: 60px;
    flex: 1;
`;

export default RatingModificationHistory;
